import { usePowerSyncChange } from '@/renderer/lib/powersync/ipcBridge';
import { SalesTab } from '@/renderer/types/sales';
import { PowerSyncDevOverlay } from '@components/common/PowerSyncDevOverlay';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@components/ui';
import { useAppDispatch, useAppSelector } from '@hooks/useRedux';
import { useSyncStatus } from '@hooks/useSyncStatus';
import { BusinessType } from '@shared/types';
import {
  selectCurrentUser,
  selectIsSubscriptionSuspended,
  selectOutletId,
} from '@store/auth/authSelectors';
import {
  fetchOutletPrintersThunk,
  fetchPrinterDeviceIdThunk,
  fetchSectionPrinterMappingsThunk,
  fetchTakeAwayPrinterMappingsThunk,
  selectPrinterDeviceId,
} from '@store/printers';
import {
  fetchTakeAwayOrdersForOutlet,
  selectTakeAwayOrders,
} from '@store/runningOrder';
import { fetchSections, fetchTables } from '@store/sections/sectionThunks';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Navigate } from 'react-router-dom';
import { useOnboardingDriverTour } from '../../hooks/useOnboardingDriverTour';
import {
  createTourElementResolver,
  ONBOARDING_TOUR_TARGETS,
  onboardingTourSelector,
} from '../../services/onboardingTourService';
import { BillingDaysCounter } from './BillingDaysCounter';
import { DiningPanel } from './DiningPanel';
import { MenuSection } from './MenuSection';
import { OrderActionButtons } from './OrderActionButtons';
import { OrderDetailPanel } from './OrderDetailPanel';
import { RunningOrderLiveSync } from './RunningOrderLiveSync';
import { RunningOrderModals } from './RunningOrderModals';
import { SalesNavbar } from './SalesNavbar';
import { SplitItemsModal } from './SplitItemsModal';
import { SubscriptionGracePeriodBanner } from './SubscriptionGracePeriodBanner';
import { TakeAwayPanel } from './TakeAwayPanel';
import { BILLING_PAGE_PATH } from '../../util/renderConstants';

export const SalesPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const outletId = useAppSelector(selectOutletId);
  const currentUser = useAppSelector(selectCurrentUser);
  const isSubscriptionSuspended = useAppSelector(selectIsSubscriptionSuspended);
  const takeAwayOrders = useAppSelector(selectTakeAwayOrders);
  const printerDeviceId = useAppSelector(selectPrinterDeviceId);
  const [activeTab, setActiveTab] = useState<SalesTab>('dining');
  const isRestaurantOutlet =
    currentUser?.outlet?.business_type === BusinessType.RESTAURANT_BAR;

  const onboardingTourSteps = useMemo(
    () => [
      {
        element: onboardingTourSelector(ONBOARDING_TOUR_TARGETS.salesPage),
        popover: {
          title: 'This is your live sales workspace',
          description:
            'Orders are opened from tables on the left, items are added from the center, and actions are completed from the right.',
          side: 'bottom' as const,
        },
      },
      {
        element: createTourElementResolver([
          onboardingTourSelector(ONBOARDING_TOUR_TARGETS.salesDiningTab),
          onboardingTourSelector(ONBOARDING_TOUR_TARGETS.salesDiningPanel),
        ]),
        popover: {
          title: 'Start from Dining',
          description:
            'For restaurant orders, begin in Dining so you can choose a section and table.',
          side: 'right' as const,
        },
      },
      {
        element: createTourElementResolver([
          onboardingTourSelector(ONBOARDING_TOUR_TARGETS.salesPrimaryTable),
          onboardingTourSelector(ONBOARDING_TOUR_TARGETS.salesDiningPanel),
        ]),
        popover: {
          title: 'Select a table to open the order',
          description:
            'Click a table here. If there is no active order yet, the system will ask you to confirm creating one first.',
          side: 'right' as const,
        },
      },
      {
        element: onboardingTourSelector(ONBOARDING_TOUR_TARGETS.salesMenuPanel),
        popover: {
          title: 'Add menu items to the order',
          description:
            'Select an item in the center panel, set quantity and notes, then confirm to add it to the working order.',
          side: 'left' as const,
        },
      },
      {
        element: createTourElementResolver([
          onboardingTourSelector(ONBOARDING_TOUR_TARGETS.salesActionWaiter),
          onboardingTourSelector(ONBOARDING_TOUR_TARGETS.salesActionPanel),
        ]),
        popover: {
          title: 'Assign a waiter',
          description:
            'Use Waiter to assign the staff member responsible for this table after items have been added.',
          side: 'left' as const,
        },
      },
      {
        element: createTourElementResolver([
          onboardingTourSelector(ONBOARDING_TOUR_TARGETS.salesActionKOT),
          onboardingTourSelector(ONBOARDING_TOUR_TARGETS.salesActionPanel),
        ]),
        popover: {
          title: 'Print the KOT',
          description:
            'Use KOT to send pending items to the kitchen or department printers configured earlier.',
          side: 'left' as const,
        },
      },
      {
        element: createTourElementResolver([
          onboardingTourSelector(ONBOARDING_TOUR_TARGETS.salesActionBill),
          onboardingTourSelector(ONBOARDING_TOUR_TARGETS.salesActionSettle),
          onboardingTourSelector(ONBOARDING_TOUR_TARGETS.salesActionPanel),
        ]),
        popover: {
          title: 'Bill, settle, shift, and discount',
          description:
            'This action panel is where you print bills, settle payments, shift orders to another table, and apply discounts when needed.',
          side: 'left' as const,
        },
      },
    ],
    [],
  );

  useOnboardingDriverTour({
    stepId: 'first_order',
    steps: onboardingTourSteps,
  });

  useEffect(() => {
    dispatch(fetchPrinterDeviceIdThunk());
  }, [dispatch]);

  useEffect(() => {
    if (!isRestaurantOutlet && activeTab !== 'takeaway') {
      setActiveTab('takeaway');
    }
  }, [activeTab, isRestaurantOutlet]);

  const { hasSynced } = useSyncStatus();
  const prevHasSynced = useRef(hasSynced);

  const reloadSalesBootstrapData = useCallback(() => {
    if (!outletId) return;

    if (isRestaurantOutlet) {
      dispatch(fetchSections(outletId));
      dispatch(fetchTables(outletId));
      dispatch(fetchSectionPrinterMappingsThunk(outletId));
    }

    dispatch(fetchTakeAwayOrdersForOutlet(outletId));
    dispatch(fetchOutletPrintersThunk(outletId));
  }, [dispatch, isRestaurantOutlet, outletId]);

  useEffect(() => {
    reloadSalesBootstrapData();
  }, [reloadSalesBootstrapData]);

  // Reload all sales bootstrap data when PowerSync completes its first sync.
  // This covers the case where the user lands on /sales before hasSynced=true
  // or when the overlay disappears and the page needs its data refreshed.
  useEffect(() => {
    if (!prevHasSynced.current && hasSynced) {
      reloadSalesBootstrapData();
    }
    prevHasSynced.current = hasSynced;
  }, [hasSynced, reloadSalesBootstrapData]);

  usePowerSyncChange(
    [
      'outlet_sections',
      'outlet_tables',
      'orders',
      'order_items',
      'outlet_printers',
      'section_printer_mappings',
      'takeaway_printer_mappings',
      'order_settlements',
      // Also reload when menu data changes so new items appear immediately
      'menu_items',
      'menu_categories',
      'departments',
    ],
    () => {
      reloadSalesBootstrapData();
    },
  );

  useEffect(() => {
    if (!outletId || !printerDeviceId) return;

    dispatch(
      fetchTakeAwayPrinterMappingsThunk({
        outletId,
        deviceId: printerDeviceId,
      }),
    );
  }, [dispatch, outletId, printerDeviceId]);

  if (isSubscriptionSuspended) {
    return <Navigate to={BILLING_PAGE_PATH} replace />;
  }

  return (
    <div
      className="flex h-screen flex-col overflow-hidden bg-white text-black"
      data-onboarding-tour={ONBOARDING_TOUR_TARGETS.salesPage}
    >
      <SalesNavbar />

      <SubscriptionGracePeriodBanner />

      <div className="flex min-h-0 flex-1 overflow-hidden">
        <aside className="app-bg flex w-[240px] min-w-[240px] flex-col border-r border-gray-200">
          <div className="app-bg flex h-11 items-center border-b border-gray-200 px-3">
            <BillingDaysCounter />
          </div>

          <Tabs
            value={activeTab}
            onValueChange={(value) => setActiveTab(value as SalesTab)}
            className="flex min-h-0 flex-1 flex-col"
          >
            <TabsList className="app-bg h-11 w-full gap-2 rounded-none border-b border-gray-200 px-2 py-1">
              {isRestaurantOutlet ? (
                <TabsTrigger
                  value="dining"
                  data-onboarding-tour={ONBOARDING_TOUR_TARGETS.salesDiningTab}
                  className="h-full flex-1 rounded border border-gray-200 bg-white px-2 text-xs font-semibold uppercase tracking-wide text-black data-[state=active]:border-none data-[state=active]:bg-primary data-[state=active]:text-white"
                >
                  Dining
                </TabsTrigger>
              ) : null}

              <TabsTrigger
                value="takeaway"
                className="h-full flex-1 rounded border border-gray-200 bg-white px-2 text-xs font-semibold uppercase tracking-wide text-black data-[state=active]:border-none data-[state=active]:bg-primary data-[state=active]:text-white"
              >
                {isRestaurantOutlet ? (
                  <>
                    <span className="mr-1 rounded bg-gray-200 px-1 text-xs font-semibold leading-none text-black">
                      {takeAwayOrders.length}
                    </span>
                    T-away
                  </>
                ) : (
                  'Orders'
                )}
              </TabsTrigger>
            </TabsList>

            {isRestaurantOutlet ? (
              <TabsContent
                value="dining"
                className="min-h-0 flex-1 overflow-y-auto"
              >
                <DiningPanel />
              </TabsContent>
            ) : null}

            <TabsContent
              value="takeaway"
              className="min-h-0 flex-1 overflow-y-auto"
            >
              <TakeAwayPanel />
            </TabsContent>
          </Tabs>
        </aside>

        <main className="flex flex-1 min-w-[400px] flex-col overflow-hidden border-r border-gray-200 bg-white">
          <MenuSection />
        </main>

        <aside className="flex w-[360px] min-w-[340px] flex-col border-r border-gray-200 bg-white">
          <OrderDetailPanel />
        </aside>

        <aside className="app-bg h-full w-[120px] min-w-[120px] border-l border-gray-200 p-2">
          <OrderActionButtons />
        </aside>
      </div>

      <RunningOrderLiveSync />
      <RunningOrderModals />
      <PowerSyncDevOverlay />
      <SplitItemsModal />
    </div>
  );
};
